import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'info',
  isLoading = false,
}) => {
  const { t } = useTranslation('common');

  const resolvedConfirmText = confirmText || t('confirm');
  const resolvedCancelText = cancelText || t('cancel');

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  return (
    <div
      className='confirmation-modal-overlay'
      onClick={handleOverlayClick}
      role='dialog'
      aria-modal='true'
      aria-labelledby='modal-title'
    >
      <div
        className={`confirmation-modal-content confirmation-modal-${variant}`}
      >
        <div className='confirmation-modal-header'>
          <h2
            id='modal-title'
            className='confirmation-modal-title'
            style={{
              fontFamily: 'Lexend Deca, sans-serif',
            }}
          >
            {title}
          </h2>
          {!isLoading && (
            <button
              onClick={onClose}
              className='confirmation-modal-close'
              aria-label={t('closeModal')}
            >
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <line x1='18' y1='6' x2='6' y2='18'></line>
                <line x1='6' y1='6' x2='18' y2='18'></line>
              </svg>
            </button>
          )}
        </div>

        <div className='confirmation-modal-body'>
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>

        <div className='confirmation-modal-footer'>
          <button
            onClick={onClose}
            className='btn btn-secondary'
            disabled={isLoading}
          >
            {resolvedCancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`btn btn-primary`}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full'></div>
                {t('processing')}
              </span>
            ) : (
              resolvedConfirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
